import { Injectable } from "@angular/core";
import { InvoiceLight } from "src/app/backend/models/invoice/invoice";
import { Quote, QuoteLight } from "src/app/backend/models/quote/quote";
import { Prescription } from "src/app/backend/models/prescription/prescription";
import {
  getInvoiceFileName,
  getPaymentReceiptFileName
} from "src/app/support/invoice";
import { getQuoteFileName } from "src/app/support/quote";
import { getPrescriptionFileName } from "src/app/support/filename";
import { getHealthRecordFileName } from "src/app/support/health-record";
import { getSignatureFileName } from "src/app/backend/models/signature/signature";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";

export enum FileType {
  Invoice = "invoice",
  Quote = "quote",
  Prescription = "prescription",
  Document = "document",
  HealthRecord = "health-record",
  PaymentReceipt = "payment-receipt",
  Signature = "signature",
  Attachment = "attachment",
  MergedInvoices = "merged-invoices"
}

export type FileNamingContext =
  | { invoice: InvoiceLight }
  | { quote: Quote }
  | { prescription: Prescription }
  | { attachmentName: string };

export interface FileNamingParams {
  fileType: FileType;
  context?: FileNamingContext;
  omitDate?: boolean;
  omitFileExtension?: boolean;
  format?: "short" | "full";
}

@Injectable()
export class FileNamingService {
  getFileName(params: FileNamingParams): string {
    const { fileType, context } = params;

    switch (fileType) {
      case FileType.Invoice: {
        const invoice: InvoiceLight | undefined =
          context && "invoice" in context ? context.invoice : undefined;

        if (!invoice) {
          return "document.pdf";
        }

        if (params.format === "short") {
          const prefix: string = invoice.isDraft
            ? sntTranslate(marker("Notula"))
            : `${sntTranslate(marker("Fattura"))}_${invoice.number}`;

          return params.omitFileExtension ? prefix : `${prefix}.pdf`;
        }

        return getInvoiceFileName(
          invoice,
          params.omitDate ?? false,
          params.omitFileExtension ?? false,
          false
        );
      }
      case FileType.Quote: {
        const quote: QuoteLight | undefined =
          context && "quote" in context ? context.quote : undefined;

        if (!quote) {
          return "document.pdf";
        }

        return getQuoteFileName(
          quote,
          params.omitDate ?? false,
          params.omitFileExtension ?? false
        );
      }
      case FileType.Prescription: {
        const prescription: Prescription | undefined =
          context && "prescription" in context
            ? context.prescription
            : undefined;

        if (!prescription) {
          return "document.pdf";
        }

        return getPrescriptionFileName(prescription);
      }
      case FileType.HealthRecord: {
        return getHealthRecordFileName(params.omitFileExtension ?? false);
      }
      case FileType.PaymentReceipt: {
        const invoice =
          context && "invoice" in context ? context.invoice : undefined;
        if (!invoice) {
          return "document.pdf";
        }

        return getPaymentReceiptFileName(invoice);
      }
      case FileType.Signature: {
        return getSignatureFileName(params.omitFileExtension ?? false);
      }
      case FileType.Attachment: {
        const attachmentName =
          context && "attachmentName" in context
            ? context.attachmentName
            : undefined;

        return attachmentName || "document.pdf";
      }
      case FileType.MergedInvoices: {
        return "Fatture.pdf";
      }
      case FileType.Document:
      default: {
        return "document.pdf";
      }
    }
  }
}
